<template>
    <div class="voa-page relative min-h-screen w-full">
        <div class="voa-page__glow" aria-hidden="true"></div>
        <div class="container mx-auto px-4 sm:px-8 py-8 relative">

            <nav class="voa-breadcrumb mb-6">
                <router-link to="/dashboard" class="voa-breadcrumb__link">Dashboard</router-link>
                <span class="voa-breadcrumb__sep">/</span>
                <router-link to="/volunteering-and-rewards-hub" class="voa-breadcrumb__link">Volunteering &amp; Rewards Hub</router-link>
                <span class="voa-breadcrumb__sep">/</span>
                <router-link
                    v-if="opportunity"
                    :to="'/volunteering-and-rewards-hub/' + token"
                    class="voa-breadcrumb__link"
                >
                    {{ opportunity.title }}
                </router-link>
                <span v-else class="voa-breadcrumb__link">Opportunity</span>
                <span class="voa-breadcrumb__sep">/</span>
                <span class="voa-breadcrumb__current">Applications</span>
            </nav>

            <div v-if="pageLoading" class="flex justify-center py-20">
                <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
            </div>

            <template v-else-if="opportunity">
                <header class="voa-hero mb-6">
                    <div class="voa-hero__accent" aria-hidden="true"></div>
                    <div class="voa-hero__inner">
                        <div class="voa-hero__topline">
                            <span class="voa-hero__kicker">
                                <font-awesome-icon :icon="['fas', 'clipboard-list']" />
                                Opportunity Applications
                            </span>
                            <div class="voa-hero__actions">
                                <router-link :to="'/volunteering-and-rewards-hub/' + token" class="voa-btn-ghost">
                                    <font-awesome-icon :icon="['fas', 'eye']" />
                                    <span>View Opportunity</span>
                                </router-link>
                                <router-link to="/volunteering-and-rewards-hub" class="voa-btn-ghost">
                                    <font-awesome-icon :icon="['fas', 'arrow-left']" />
                                    <span>Back to hub</span>
                                </router-link>
                            </div>
                        </div>
                        <h1 class="voa-hero__title">{{ opportunity.title }}</h1>
                        <div class="voa-hero__meta">
                            <span class="voa-badge" :class="'voa-badge--' + opportunity.status">{{ formatStatus(opportunity.status) }}</span>
                            <span class="voa-meta-pill">
                                <font-awesome-icon :icon="['fas', 'users']" />
                                {{ opportunity.approved_applications_count || 0 }} approved
                                <span class="voa-meta-pill__muted">/ {{ opportunity.max_volunteers || '∞' }}</span>
                            </span>
                            <span v-if="opportunity.pending_applications_count" class="voa-meta-pill voa-meta-pill--warn">
                                {{ opportunity.pending_applications_count }} pending
                            </span>
                        </div>
                    </div>
                </header>

                <section class="voa-panel">
                    <div class="voa-panel__header">
                        <div>
                            <h2 class="voa-panel__title">Applications</h2>
                            <p class="voa-panel__subtitle">Review student applications for this opportunity.</p>
                        </div>
                        <div class="voa-filters">
                            <select v-model="statusFilter" class="voa-select" @change="onFiltersChange">
                                <option value="">All Statuses</option>
                                <option value="pending">Pending</option>
                                <option value="approved">Approved</option>
                                <option value="returned">Returned</option>
                                <option value="rejected">Rejected</option>
                                <option value="withdrawn">Withdrawn</option>
                            </select>
                            <div class="voa-search-wrap">
                                <font-awesome-icon :icon="['fas', 'search']" class="voa-search-icon" />
                                <input
                                    v-model="studentSearch"
                                    type="text"
                                    placeholder="Search student..."
                                    class="voa-input voa-search-input"
                                    @keyup.enter="onFiltersChange"
                                />
                            </div>
                            <button type="button" class="voa-btn-primary" @click="onFiltersChange">Search</button>
                        </div>
                    </div>

                    <div v-if="listLoading" class="voa-panel__body">
                        <table-shimmer :titleHeaders="['#', 'Student', 'Status', 'Submitted', 'Actions']" :rows="6" />
                    </div>
                    <div v-else-if="applications.length === 0" class="voa-empty">
                        <font-awesome-icon :icon="['fas', 'user-clock']" class="voa-empty__icon" />
                        <p class="voa-empty__title">No applications found</p>
                        <p class="voa-empty__text">Try adjusting the status filter or search term.</p>
                    </div>
                    <div v-else>
                        <div class="overflow-x-auto">
                            <table class="min-w-full text-left text-sm">
                                <thead>
                                    <tr class="voa-thead-row">
                                        <th class="voa-th voa-th-num">#</th>
                                        <th class="voa-th">Student</th>
                                        <th class="voa-th">Status</th>
                                        <th class="voa-th">Submitted</th>
                                        <th class="voa-th">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(app, index) in applications" :key="app.id" class="voa-row">
                                        <td class="voa-td voa-td-num">{{ paginatedRowNumber(paginator, index) }}</td>
                                        <td class="voa-td">
                                            <p
                                                v-if="app.student_id"
                                                class="voa-student-name m-0"
                                                @click.stop="viewStudentProfile(app.student_id)"
                                            >
                                                {{ app.user && app.user.name ? app.user.name : app.student_id }}
                                            </p>
                                            <p v-else class="font-semibold text-gray-900 m-0">
                                                {{ app.user && app.user.name ? app.user.name : '—' }}
                                            </p>
                                            <p class="text-xs text-gray-500 m-0 mt-0.5">{{ app.student_id }}</p>
                                            <p v-if="app.user && app.user.email" class="text-xs text-gray-400 m-0 mt-0.5">{{ app.user.email }}</p>
                                        </td>
                                        <td class="voa-td">
                                            <span class="voa-badge" :class="'voa-badge--' + app.status">{{ formatStatus(app.status) }}</span>
                                        </td>
                                        <td class="voa-td text-gray-500 whitespace-nowrap">{{ formatDateTime(app.submitted_at) }}</td>
                                        <td class="voa-td">
                                            <router-link
                                                :to="'/volunteering-and-rewards-hub/' + token + '?application=' + app.id"
                                                class="voa-text-link"
                                            >
                                                Review
                                            </router-link>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div class="voa-panel__footer">
                            <pagination v-if="paginator" :paginator-info="paginator" @page-changed="onPageChange" />
                        </div>
                    </div>
                </section>
            </template>
        </div>

        <transition name="fade" mode="out-in">
            <base-full-modal
                v-if="showStudentProfileFullModal"
                title="Student Profile"
                @close="closeStudentProfileModal"
            >
                <student-profile :student-id="selectedStudentId"></student-profile>
            </base-full-modal>
        </transition>
    </div>
</template>

<script>
import gql from 'graphql-tag';
import Pagination from '@/components/shared/pagination.vue';
import TableShimmer from '@/components/shared/TableShimmer.vue';
import StudentProfile from '@/pages/student-profile/index.vue';

const OPPORTUNITY_QUERY = gql`
  query VolunteeringOpportunityByToken($token: String!) {
    volunteeringOpportunityByToken(token: $token) {
      error error_code message
      data {
        id token title status max_volunteers
        pending_applications_count approved_applications_count
      }
    }
  }
`;

const APPLICATIONS_QUERY = gql`
  query VolunteeringOpportunityApplications(
    $first: Int
    $page: Int
    $status: String
    $studentSearch: String
    $opportunityId: ID
  ) {
    volunteeringApplications(
      first: $first
      page: $page
      status: $status
      student_search: $studentSearch
      opportunity_id: $opportunityId
    ) {
      error error_code message
      paginatorInfo { total currentPage lastPage perPage firstItem }
      data {
        id status student_id submitted_at
        user { id name email }
        opportunity { id token title }
      }
    }
  }
`;

export default {
    name: 'VolunteeringOpportunityApplications',
    components: { Pagination, TableShimmer, StudentProfile },
    props: {
        token: { type: String, required: true },
    },
    data() {
        return {
            pageLoading: true,
            listLoading: false,
            opportunity: null,
            applications: [],
            paginator: null,
            page: 1,
            statusFilter: '',
            studentSearch: '',
            showStudentProfileFullModal: false,
            selectedStudentId: null,
        };
    },
    created() {
        if (!this.hasPermission('volunteering-and-rewards-hub-module')) {
            this.$router.replace('/no-permission');
            return;
        }
        if (!this.hasPermission('volunteering-application-index')) {
            this.$router.replace('/no-permission');
            return;
        }
        this.bootstrap();
    },
    methods: {
        hasPermission(p) {
            return (this.$store.getters.permissions || '').split(',').includes(p);
        },
        viewStudentProfile(studentId) {
            if (!studentId) return;
            this.selectedStudentId = studentId;
            this.showStudentProfileFullModal = true;
        },
        closeStudentProfileModal() {
            this.showStudentProfileFullModal = false;
            this.selectedStudentId = null;
        },
        async bootstrap() {
            this.pageLoading = true;
            await this.fetchOpportunity();
            if (this.opportunity) {
                await this.fetchApplications();
            }
            this.pageLoading = false;
        },
        async fetchOpportunity() {
            try {
                const { data } = await this.$apollo.query({
                    query: OPPORTUNITY_QUERY,
                    variables: { token: this.token },
                    fetchPolicy: 'network-only',
                });
                const result = data.volunteeringOpportunityByToken;
                if (!result || result.error) {
                    if (result && result.error_code === 403) {
                        this.$router.replace('/no-permission');
                        return;
                    }
                    if (result && result.error_code === 404) {
                        this.$router.replace('/volunteering-and-rewards-hub');
                        return;
                    }
                    if (this.$swal) {
                        this.$swal.fire({
                            icon: 'error',
                            title: 'Error',
                            text: (result && result.message) || 'Unable to load opportunity.',
                        });
                    }
                    return;
                }
                this.opportunity = result.data;
            } catch (e) {
                if (this.$swal) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: (e && e.message) || 'Unable to load opportunity.',
                    });
                }
            }
        },
        async fetchApplications() {
            if (!this.opportunity || !this.opportunity.id) return;
            this.listLoading = true;
            try {
                const { data } = await this.$apollo.query({
                    query: APPLICATIONS_QUERY,
                    variables: {
                        first: 10,
                        page: this.page,
                        status: this.statusFilter || null,
                        studentSearch: this.studentSearch || null,
                        opportunityId: String(this.opportunity.id),
                    },
                    fetchPolicy: 'network-only',
                });
                const result = data.volunteeringApplications;
                if (!result || result.error) {
                    if (result && result.error_code === 403) {
                        this.$router.replace('/no-permission');
                        return;
                    }
                    this.applications = [];
                    this.paginator = null;
                    return;
                }
                this.applications = result.data || [];
                this.paginator = result.paginatorInfo;
            } catch (e) {
                this.applications = [];
                this.paginator = null;
            }
            this.listLoading = false;
        },
        onFiltersChange() {
            this.page = 1;
            this.fetchApplications();
        },
        onPageChange(newPage) {
            this.page = newPage;
            this.fetchApplications();
        },
        paginatedRowNumber(paginator, index) {
            if (!paginator || !paginator.firstItem) return index + 1;
            return paginator.firstItem + index;
        },
        formatStatus(s) {
            return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
        },
        formatDateTime(d) {
            return d ? new Date(d).toLocaleString() : '';
        },
    },
};
</script>

<style scoped lang="scss">
.voa-page {
    background: linear-gradient(180deg, #f8fafc 0%, #eef4ff 48%, #f8fafc 100%);
}
.voa-page__glow {
    pointer-events: none;
    position: absolute;
    inset: 0 0 auto 0;
    height: 18rem;
    background:
        radial-gradient(ellipse 70% 60% at 15% 0%, rgba(37, 99, 235, 0.12), transparent 70%),
        radial-gradient(ellipse 50% 50% at 90% 10%, rgba(14, 165, 233, 0.08), transparent 65%);
}

.voa-breadcrumb {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    font-size: 0.8125rem;
    color: #64748b;
}
.voa-breadcrumb__link {
    color: #64748b;
    font-weight: 500;
    text-decoration: none;
    max-width: 14rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    &:hover { color: #1d4ed8; }
}
.voa-breadcrumb__sep {
    margin: 0 0.4rem;
    color: #cbd5e1;
}
.voa-breadcrumb__current {
    color: #0f172a;
    font-weight: 600;
}

.voa-hero {
    position: relative;
    overflow: hidden;
    border-radius: 1.15rem;
    background: #fff;
    border: 1px solid #e2e8f0;
    box-shadow: 0 10px 30px -18px rgba(15, 23, 42, 0.28);
}
.voa-hero__accent {
    height: 0.35rem;
    background: linear-gradient(90deg, #1e3a8a 0%, #2563eb 55%, #38bdf8 100%);
}
.voa-hero__inner {
    padding: 1.35rem 1.4rem 1.25rem;
}
.voa-hero__topline {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.65rem;
}
.voa-hero__kicker {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #2563eb;
}
.voa-hero__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}
.voa-hero__title {
    margin: 0;
    font-size: 1.65rem;
    font-weight: 800;
    line-height: 1.25;
    letter-spacing: -0.02em;
    color: #0f172a;
}
.voa-hero__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.85rem;
}

.voa-meta-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.28rem 0.65rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 650;
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
}
.voa-meta-pill__muted { color: #64748b; font-weight: 500; }
.voa-meta-pill--warn {
    background: #fff7ed;
    color: #c2410c;
    border-color: #fed7aa;
}

.voa-panel {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 1.1rem;
    overflow: hidden;
    box-shadow: 0 8px 24px -18px rgba(15, 23, 42, 0.35);
}
.voa-panel__header {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.15rem 1.35rem;
    border-bottom: 1px solid #f1f5f9;
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}
.voa-panel__title {
    margin: 0;
    font-size: 1rem;
    font-weight: 750;
    color: #0f172a;
}
.voa-panel__subtitle {
    margin: 0.2rem 0 0;
    font-size: 0.8125rem;
    color: #64748b;
}
.voa-panel__body { padding: 1.25rem 1.35rem; }
.voa-panel__footer {
    padding: 0.85rem 1.25rem;
    border-top: 1px solid #f1f5f9;
    background: #fafbfc;
}

.voa-filters {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.55rem;
}
.voa-select,
.voa-input {
    padding: 0.5rem 0.75rem;
    font-size: 0.8125rem;
    color: #1e293b;
    background: #fff;
    border: 1px solid #cbd5e1;
    border-radius: 0.55rem;
    &:focus {
        outline: none;
        border-color: #2563eb;
        box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
    }
}
.voa-search-wrap {
    position: relative;
}
.voa-search-icon {
    position: absolute;
    left: 0.7rem;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    font-size: 0.75rem;
}
.voa-search-input {
    padding-left: 2rem;
    min-width: 11rem;
}

.voa-btn-primary,
.voa-btn-ghost {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    min-height: 2.35rem;
    padding: 0.45rem 0.9rem;
    border-radius: 0.55rem;
    font-size: 0.8125rem;
    font-weight: 650;
    cursor: pointer;
    border: 1px solid transparent;
    text-decoration: none;
}
.voa-btn-primary {
    color: #fff;
    background: linear-gradient(180deg, #1e3a8a 0%, #2563eb 100%);
    box-shadow: 0 8px 18px -10px rgba(37, 99, 235, 0.65);
}
.voa-btn-ghost {
    color: #475569;
    background: #f8fafc;
    border-color: #e2e8f0;
    &:hover { background: #f1f5f9; color: #0f172a; }
}

.voa-thead-row {
    background: #f8fafc;
}
.voa-th {
    padding: 0.75rem 1rem;
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #64748b;
    border-bottom: 1px solid #e2e8f0;
}
.voa-th-num { width: 3rem; }
.voa-row:hover { background: #f8fbff; }
.voa-td {
    padding: 0.85rem 1rem;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
}
.voa-td-num { color: #94a3b8; font-weight: 600; }

.voa-student-name {
    font-weight: 650;
    color: #0f172a;
    cursor: pointer;
    transition: color 0.15s ease;
    &:hover {
        color: #2563eb;
        text-decoration: underline;
    }
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.voa-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.22rem 0.55rem;
    border-radius: 9999px;
    font-size: 0.6875rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.02em;
}
.voa-badge--draft { background: #f1f5f9; color: #475569; }
.voa-badge--published { background: #dbeafe; color: #1d4ed8; }
.voa-badge--closed { background: #fee2e2; color: #b91c1c; }
.voa-badge--completed { background: #d1fae5; color: #047857; }
.voa-badge--archived { background: #e2e8f0; color: #64748b; }
.voa-badge--pending { background: #fef3c7; color: #b45309; }
.voa-badge--approved { background: #d1fae5; color: #047857; }
.voa-badge--rejected { background: #fee2e2; color: #b91c1c; }
.voa-badge--returned { background: #e0e7ff; color: #4338ca; }
.voa-badge--withdrawn { background: #f1f5f9; color: #64748b; }

.voa-text-link {
    display: inline-flex;
    align-items: center;
    padding: 0.35rem 0.65rem;
    border-radius: 0.5rem;
    border: 1px solid #bfdbfe;
    background: #eff6ff;
    color: #1d4ed8;
    font-size: 0.75rem;
    font-weight: 650;
    text-decoration: none;
    &:hover {
        background: #dbeafe;
        border-color: #93c5fd;
        color: #1e40af;
    }
}

.voa-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 3rem 1.5rem;
    color: #64748b;
}
.voa-empty__icon {
    font-size: 1.75rem;
    color: #94a3b8;
    margin-bottom: 0.75rem;
}
.voa-empty__title {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: #0f172a;
}
.voa-empty__text {
    margin: 0.35rem 0 0;
    font-size: 0.875rem;
}
</style>
